import { CommonModule } from "@angular/common";
import { Component, Input, OnInit, signal } from "@angular/core";
import { FormControl, ReactiveFormsModule } from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { ngxEditorConfig } from "@config/angular-editor.config";
import { AngularEditorModule } from "@kolkov/angular-editor";
import type { IApiResponse } from "@models/index";
import { RequestService } from "@services/index";

/**
 * The shop's own house rules, as shown on its storefront page.
 *
 * The same row the merchant edits in their own portal - this is here so a shop
 * can be helped with its wording, or corrected, without asking the merchant to
 * log in. Last edit wins; there is no separate admin copy to reconcile.
 *
 * Same editor and config as SEO management's page content, so the markup a
 * merchant sees and the markup an admin sees come out of the same tool.
 */
@Component({
  selector: "fh-vendor-shop-guideline",
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, MatButtonModule, AngularEditorModule],
  template: `
    <div class="card">
      <div class="card-header card-header-danger">
        <h4 class="card-title">Merchant Guidelines</h4>
        <p class="card-category">
          Shown on this merchant's shop page, behind a "Merchant Guidelines" link. Leave it
          empty and no link appears.
        </p>
      </div>
      <div class="card-body">
        <angular-editor
          [formControl]="contentControl"
          [placeholder]="'Ordering, packing, live arrival, collection — whatever a shopper should read before buying.'"
          [config]="editorConfig">
        </angular-editor>

        @if (updatedAt()) {
          <p class="text-muted mt-2" style="font-size: 12px;">
            Last updated {{ updatedAt() | date: 'medium' }}
          </p>
        }

        <div class="mt-3">
          <button mat-raised-button color="primary" (click)="save()" [disabled]="saving()">
            {{ saving() ? 'Saving…' : 'Save' }}
          </button>
        </div>

        @if (success()) {
          <div class="alert alert-success mt-3">{{ success() }}</div>
        }
        @if (error()) {
          <div class="alert alert-danger mt-3">{{ error() }}</div>
        }
      </div>
    </div>
  `,
})
export class VendorShopGuidelineComponent implements OnInit {
  @Input() vendorId!: string;

  readonly contentControl = new FormControl<string>("");

  editorConfig = {
    ...ngxEditorConfig,
    enableToolbar: true,
    showToolbar: true,
    // Matching SEO management: the buttons left out are the ones whose output
    // the storefront's sanitiser strips anyway, so offering them would let
    // someone style something that silently arrives plain.
    toolbarHiddenButtons: [
      ["subscript", "superscript", "indent", "outdent", "fontName"],
      ["fontSize", "textColor", "backgroundColor", "customClasses", "insertVideo"],
    ],
  };

  saving = signal(false);
  success = signal("");
  error = signal("");
  updatedAt = signal<string | null>(null);

  constructor(private requestService: RequestService) {}

  ngOnInit(): void {
    if (!this.vendorId) return;
    this.requestService
      .get<IApiResponse>(`/api/v2/vendor/${this.vendorId}/shop-guideline`)
      .subscribe({
        next: (res) => {
          if (res.status) {
            const data = res.data as { content: string; updatedAt: string | null };
            this.contentControl.setValue(data.content ?? "");
            this.updatedAt.set(data.updatedAt);
          }
        },
        error: () => this.error.set("Could not load this vendor's merchant guideline"),
      });
  }

  save(): void {
    if (this.saving()) return;
    this.saving.set(true);
    this.success.set("");
    this.error.set("");

    this.requestService
      .put<IApiResponse>(`/api/v2/vendor/${this.vendorId}/shop-guideline`, {
        // An editor emptied by hand reports null, which would be stored as the
        // string "null" if passed straight through.
        content: this.contentControl.value ?? "",
      })
      .subscribe({
        next: (res) => {
          this.saving.set(false);
          if (res.status) {
            this.success.set("Merchant guidelines saved");
            const data = res.data as { updatedAt: string | null } | undefined;
            if (data?.updatedAt) this.updatedAt.set(data.updatedAt);
          } else {
            this.error.set(res.message || "Could not save the merchant guideline");
          }
        },
        error: () => {
          this.saving.set(false);
          this.error.set("Could not save the merchant guideline");
        },
      });
  }
}
